import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileText, Mail, Globe } from 'lucide-react';
import SITE_CONFIG from '../config/siteConfig';

const TermsPage = () => {
  return (
    <>
      <Helmet>
        <title>Terms and Conditions - AI Carousel Maker</title>
        <link rel="canonical" href={`${SITE_CONFIG.siteUrl}/terms`} />
        <meta name="description" content="Terms and Conditions for using AI Carousel Maker. Read our terms of service, user responsibilities, and platform guidelines." />
        <meta property="og:title" content="Terms and Conditions - AI Carousel Maker" />
        <meta property="og:url" content={`${SITE_CONFIG.siteUrl}/terms`} />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <FileText className="h-12 w-12 text-primary-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Terms and Conditions
          </h1>
          <p className="text-gray-600">Last updated: 12 December 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Welcome to <strong>NovialCraft</strong> (the "Platform", "Service", "we", "our", or "us"), accessible at{' '}
              <a href="https://novialcraft.com" className="text-primary-600 hover:text-primary-700">
                https://novialcraft.com
              </a>{' '}
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              By accessing or using our website, applications, or services, you agree to be bound by these Terms and Conditions. 
              If you do not agree with any part of these terms, you must not use our services.
            </p>

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Eligibility</h2>
              <p className="text-gray-700 leading-relaxed">
                You must be at least 18 years old to use this platform. By using the Service, you confirm that you are 
                legally capable of entering into a binding agreement.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Account Registration</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>You may be required to create an account to access certain features.</li>
                <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
                <li>You agree to provide accurate and complete information.</li>
                <li>You are responsible for all activities conducted under your account.</li>
                <li>We reserve the right to suspend or terminate accounts that violate these terms.</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Description of Services</h2>
              <p className="text-gray-700 leading-relaxed mb-3">NovialCraft provides:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>AI-powered image generation and related creative tools</li>
                <li>Storage of user-generated content</li>
                <li>APIs and web interfaces for content creation</li>
                <li>Paid and/or free usage plans</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Service availability and features may change without prior notice.
              </p>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User-Generated Content</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Ownership</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>You retain ownership of the content you create using our platform.</li>
                <li>
                  By uploading or generating content, you grant us a non-exclusive, worldwide, royalty-free license 
                  to store, process, and display the content solely for providing the Service.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Responsibility</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                You are solely responsible for your content. You agree not to upload, generate, or distribute content that:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Is illegal, harmful, abusive, or defamatory</li>
                <li>Infringes intellectual property rights</li>
                <li>Contains explicit sexual, violent, or hateful material</li>
                <li>Violates any applicable laws or regulations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to remove content that violates these terms.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Prohibited Use</h2>
              <p className="text-gray-700 leading-relaxed mb-3">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Abuse or exploit the Service or infrastructure</li>
                <li>Attempt to bypass security or usage limits</li>
                <li>Reverse-engineer or copy platform technology</li>
                <li>Use automated tools to scrape or overload the system</li>
                <li>Use the Service for unlawful or fraudulent purposes</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Payments & Subscriptions</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Some features may require payment.</li>
                <li>All fees are displayed before purchase.</li>
                <li>Payments are non-refundable unless explicitly stated.</li>
                <li>We may change pricing at any time with prior notice.</li>
                <li>Failure to complete payment may result in service suspension.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Storage & Data</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Generated images and user files may be stored on third-party cloud infrastructure.</li>
                <li>We do not guarantee permanent storage of content.</li>
                <li>We are not responsible for loss of data due to system failures, misuse, or external events.</li>
                <li>We recommend downloading and backing up important content.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Availability & Performance</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>We strive for high availability but do not guarantee uninterrupted service.</li>
                <li>AI processing times may vary depending on system load.</li>
                <li>Temporary downtime may occur for maintenance or upgrades.</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>All platform code, designs, trademarks, logos, and branding belong to NovialCraft unless otherwise stated.</li>
                <li>You may not use our intellectual property without prior written consent.</li>
              </ul>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Our platform may integrate third-party services (e.g., payment gateways, cloud storage).
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">We are not responsible for:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Third-party outages</li>
                <li>Data policies of external services</li>
                <li>Losses caused by third-party failures</li>
              </ul>
            </section>

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We may suspend or terminate access to the Service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>If you violate these terms</li>
                <li>If required by law</li>
                <li>For security or operational reasons</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                You may stop using the Service at any time.
              </p>
            </section>

            {/* Section 12 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Disclaimer of Warranties</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                The Service is provided "as is" and "as available".
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">We make no warranties regarding:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Accuracy or reliability of generated content</li>
                <li>Fitness for a particular purpose</li>
                <li>Continuous or error-free operation</li>
              </ul>
            </section>

            {/* Section 13 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-3">To the maximum extent permitted by law:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>We are not liable for indirect, incidental, or consequential damages</li>
                <li>We are not responsible for losses arising from generated content usage</li>
                <li>Our total liability shall not exceed the amount paid by you in the last 12 months (if any)</li>
              </ul>
            </section>

            {/* Section 14 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                You agree to indemnify and hold harmless NovialCraft from any claims arising from:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Your use of the Service</li>
                <li>Your content</li>
                <li>Violation of these terms or applicable laws</li>
              </ul>
            </section>

            {/* Section 15 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms are governed by the laws of <strong>India</strong>, without regard to conflict of law principles.
              </p>
            </section>

            {/* Section 16 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update these Terms from time to time. Continued use of the Service after changes means you accept 
                the updated Terms.
              </p>
            </section>

            {/* Section 17 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For questions or legal concerns, contact us at:
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-primary-600" />
                  <a href="mailto:support@novialtechnologies.com" className="text-primary-600 hover:text-primary-700">
                    support@novialtechnologies.com
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Globe className="h-5 w-5 text-primary-600" />
                  <a href="https://novialcraft.com" className="text-primary-600 hover:text-primary-700">
                    https://novialcraft.com
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link 
            to="/" 
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default TermsPage;
